import { WidgetProps } from '@highimpact-front/remote-widget-core';
import { PlotJSON } from '@highimpact-frontend/api-types';
import { GenericErrorBoundary } from '@highimpact/dashboard-ui';
import { PermissionDecorator } from '@highimpact/react-decorators';
import SettingsIcon from '@mui/icons-material/Settings';
import { forwardRef, LegacyRef } from 'react';
import { useDashboardPermissionScope } from '../../../../hooks/useDashboardPermissionScope';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../../../../runtime/DashboardLibraryProvider';
import {
  setDeleteWidgetConfirmation,
  setWidgetConfigPanelState,
} from '../../../../state/dashboard/slice';
import { findDashboardTypeById } from '../../../../state/store-operations';
import NameHandlerComponent from '../../../NameHandlerComponent';
import UnconfiguredWidgetPlaceholder from '../../commons/UnconfiguredWidgetPlaceholder';
import Widget from '../../commons/Widget';
import { WidgetToolbar } from '../../commons/WidgetToolbar';
import { WidgetDragHandle } from '../../commons/WidgetDragHandle';

interface EmptyInitialTableProps extends WidgetProps {
  name: string;
  gridId: number;
  plotType: PlotJSON['plot_type'];
}

function EmptyInitialTable(
  {
    name,
    className,
    non_removable,
    plotType,
    gridId,
    ...props
  }: EmptyInitialTableProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const dispatch = useDashboardLibraryDispatch();
  const { site_id, project_id } = useDashboardPermissionScope();

  const { dashboardElementId } = props;
  const dashboardType = useDashboardLibrarySelector((state) =>
    gridId ? findDashboardTypeById(gridId, state.dashboard) : undefined
  );

  if (!gridId || !dashboardType)
    return <div>Tableaus de bord introuvable !</div>;

  return (
    <Widget ref={ref} {...props} className={`text-input ${className || ''}`}>
      <GenericErrorBoundary>
        <PermissionDecorator
          item="dashboard_nav_bar_widget"
          project_id={project_id}
          site_id={site_id}
          extra={{ dashboard_id: gridId }}
        >
          <div className="widget-nav">
            <p style={{ display: 'flex' }}>
              <PermissionDecorator item="dashboard_move_widget">
                <WidgetDragHandle />
              </PermissionDecorator>
              <PermissionDecorator item="dashboard_rename_widget">
                <NameHandlerComponent
                  widgetId={dashboardElementId}
                  dashboardElementId={props.widget_id}
                  dashboardId={gridId}
                  dashboardType={dashboardType}
                />
              </PermissionDecorator>
            </p>
            <div className="nav-icon">
              <WidgetToolbar
                dashboardElementId={dashboardElementId}
                dashboardId={gridId}
                // TODO: replace
                platformActions={{
                  edit: {
                    visible: true,
                    tooltipText: "Configurer l'élément",
                    permissionItem: 'dashboard_configure_widget',
                    buttonIcon: SettingsIcon,
                    onClick: () =>
                      dispatch(
                        setWidgetConfigPanelState({
                          state: 'Configuring',
                          elementType: 'table',
                          gridId: gridId,
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          gridType: dashboardType!,
                          widgetId: dashboardElementId,
                        })
                      ),
                  },
                  delete: !non_removable
                    ? {
                        visible: true,
                        tooltipText: "Supprimer l'élément",
                        permissionItem: 'dashboard_delete_widget',
                        onClick: () =>
                          dispatch(
                            setDeleteWidgetConfirmation({
                              state: 'open',
                              dashboardElementId: dashboardElementId,
                              widgetName: name ?? 'de nom inconnu',
                            })
                          ),
                      }
                    : undefined,
                }}
              />
            </div>
          </div>
        </PermissionDecorator>
        <UnconfiguredWidgetPlaceholder
          message="Le Tableau n'est pas encore configuré"
          gridId={gridId}
          gridType={dashboardType}
          widgetId={dashboardElementId}
          elementType="table"
        />
      </GenericErrorBoundary>
    </Widget>
  );
}

export default forwardRef(EmptyInitialTable);
